import React, { useState, useEffect } from 'react';
import { LockedReferenceDocumentField } from '../../../components/LockedReferenceDocumentField';
import {
  fetchRiskAssessmentReferenceDocument,
  formatRiskAssessmentReferenceDocumentText,
} from '../utils/fetchRiskAssessmentReferenceDocument';

/**
 * Reference Document at end of Add/Edit Risk Assessment form.
 * Readonly + lock; unlock with qmsEditablePassword. Snapshot stored on create.
 * In view/split panels (no setFormData), show snapshot only — do not refetch.
 */
const RiskAssessmentReferenceDocument = ({ formData, setFormData }) => {
  const canUpdateForm = typeof setFormData === 'function';
  const [isLoading, setIsLoading] = useState(
    () => canUpdateForm && !formData?.referenceDocument,
  );

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!canUpdateForm) {
        setIsLoading(false);
        return;
      }

      if (formData?.referenceDocument) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const doc = await fetchRiskAssessmentReferenceDocument();
        const text = formatRiskAssessmentReferenceDocumentText(doc);
        if (cancelled) return;
        if (text) {
          setFormData((prev) =>
            prev?.referenceDocument === text
              ? prev
              : { ...prev, referenceDocument: text },
          );
        }
      } catch {
        /* keep empty */
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [canUpdateForm, formData?.id, formData?.referenceDocument, setFormData]);

  return (
    <LockedReferenceDocumentField
      value={formData?.referenceDocument ?? ''}
      onChange={
        canUpdateForm
          ? (next) => setFormData((prev) => ({ ...prev, referenceDocument: next }))
          : undefined
      }
      isLoading={isLoading}
      recordId={formData?.id}
      inputId="risk-assessment-reference-document"
      emptyHint="No reference document found for module Risk Assessments"
    />
  );
};

export default RiskAssessmentReferenceDocument;
